<template>
  <section class="word-break">
    <!-- 國健署資料 --start -->
    <ColonPreviewBowelCleanseForms
      id="colonPreviewBowelCleanseForms"
      v-if="isColonReport"
      :examination="examination"
      :sedatives="sedatives"
    />
    <div class="border-b">
      <gastro-preview-bowel-cleanse-forms
        v-if="!isColonReport"
        :examination="examination"
        :sedatives="sedatives"
      />
    </div>
    <!-- 國健署資料 --end -->

    <!-- 1. Procedure Note + Findings article + global map -->
    <div class="border-b border-b--dashed py-3 report-top-grid">
      <div class="report-top-grid__content">
        <div class="report-top-grid__procedure">
          <h3 class="print-color subtitle-border mb-2">
            {{ $t('common.procedure_note') }}：
          </h3>
          <p class="ml-2 mb-0">{{ procedureNoteText }}</p>
        </div>

        <div class="report-top-grid__article mt-3">
          <h3 class="print-color subtitle-border mb-2">{{ $t('report_preview.findings') }}:</h3>
          <div v-if="getIsGeneratingDescriptiveParagraph" class="pa-3 d-flex align-center">
            <v-progress-circular indeterminate color="primary" class="mr-3" size="24"></v-progress-circular>
            <span>{{ $t('report.generating_descriptive_paragraph') }}</span>
          </div>
          <div v-else class="pa-3">
            <template v-if="isColonReport && colonLlmTexts.length">
              <div v-for="(text, idx) in colonLlmTexts" :key="`detail-colon-des-${idx}`">
                <div>{{ text }}</div>
              </div>
            </template>
            <template v-else-if="!isColonReport && gastroLlmEntries.length">
              <div v-for="entry in gastroLlmEntries" :key="entry[0]" class="mb-3">
                <p v-if="entry[0]" class="font-weight-bold mb-0">{{ entry[0] }}:</p>
                <div>{{ entry[1] || '-' }}</div>
              </div>
            </template>
            <p v-else class="ml-2 mb-0">{{ $t('common.nil') }}</p>
          </div>
        </div>
      </div>

      <div class="report-top-grid__map">
        <FindingInfoByOrder
          v-if="isColonReport"
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showFindingNames="false"
          :showFindingDescriptions="false"
          :showFindingImages="false"
          :showOtherImages="false"
        />
        <FindingInfoByOrgan
          v-else
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showFindingNames="false"
          :showFindingDescriptions="false"
          :showFindingImages="false"
          :showOtherImages="false"
        />
      </div>
    </div>

    <!-- 2. Impression -->
    <div class="border-b border-b--dashed">
      <h3 class="print-color subtitle-border pt-3 mb-2">{{ impressionTitle }}:</h3>
      <SummaryInfoGroup
        type="summary"
        :report-title="reportTitle"
        :findings="findings"
        hide-summary-title
      />
    </div>

    <!-- 3. Suggestion -->
    <div class="border-b border-b--dashed">
      <SummaryInfoGroup
        type="suggestion"
        :report-title="reportTitle"
        :findings="findings"
      />
    </div>

    <!-- 4. Finding descriptions disabled.
    <div class="border-b border-b--dashed py-3">
      <h3 class="print-color subtitle-border mb-2">{{ findingDescriptionsTitle }}:</h3>
      <template v-if="hasFindings">
        <FindingInfoByOrder
          v-if="isColonReport"
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showGlobalMap="false"
          :showFindingNames="false"
          :showFindingImages="false"
          :showFindingMap="false"
          :showOtherImages="false"
        />
        <FindingInfoByOrgan
          v-else
          :findings="findings"
          :imgArrange="imgArrange"
          :showHeader="false"
          :showGlobalMap="false"
          :showFindingNames="false"
          :showFindingImages="false"
          :showFindingMap="false"
          :showOtherImages="false"
        />
      </template>
      <p v-else class="pl-3">{{ $t('common.nil') }}</p>
    </div>
    -->

    <!-- 5. Medical images grouped by finding order -->
    <FindingItemImagesGroup
      class="py-3"
      :findings="findingsWithImages"
      :additionImages="[]"
      :imgArrange="imgArrange || 3"
      :title="keyImagesTitle"
    />

    <!-- 6. Other images selected for report without finding assignment -->
    <div v-if="additionalImages.length" class="border-t border-t--dashed py-3">
      <h3 class="print-color subtitle-border mb-2">{{ moreImagesTitle }}:</h3>
      <div class="ml-2 break-inside-page" style="padding: 5px">
        <FindingItemImages
          :finding="{ images: additionalImages }"
          :imgArrange="imgArrange || 3"
          :showName="false"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import SummaryInfoGroup from '@/components/SummaryInfoGroup.vue'
import GastroPreviewBowelCleanseForms from '@/components/preview/GastroPreviewBowelCleanseForms.vue'
import ColonPreviewBowelCleanseForms from '@/components/preview/ColonPreviewBowelCleanseForms.vue'
import FindingInfoByOrgan from './FindingInfoByOrgan.vue'
import FindingInfoByOrder from './FindingInfoByOrder.vue'
import FindingItemImages from '@/components/preview/FindingItemImages.vue'
import FindingItemImagesGroup from '@/components/preview/FindingItemImagesGroup.vue'

import { PRINT_STATUS_YES } from '@/utils/examinations.config.js'

export default {
  name: 'SummaryEnReport',
  components: {
    ColonPreviewBowelCleanseForms,
    GastroPreviewBowelCleanseForms,
    SummaryInfoGroup,
    FindingItemImages,
    FindingItemImagesGroup,
    FindingInfoByOrder,
    FindingInfoByOrgan,
  },
  props: {
    examination: Object,
    sedatives: Array,
    isColonReport: {
      type: Boolean,
      default: true,
    },
    isFullMode: {
      type: Boolean,
      default: true,
    },
    reportTitle: String,
    imgArrange: Number,
    findings: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters('examinations', [
      'getColonDescriptiveParagraph',
      'getGastroDescriptiveParagraph',
      'getIsGeneratingDescriptiveParagraph',
      'getInsertionLevelList'
    ]),
    procedureNoteText() {
      const procedureNote = this.examination?.examination?.procedureNote
      if (!procedureNote) return this.$t('common.nil')

      const insertionLevel = this.examination?.examination?.insertionLevel
      if (!insertionLevel) return procedureNote

      const insertion = this.getInsertionLevelList.find((item) => item.value === insertionLevel)
      return insertion ? procedureNote.replace('(位置)', insertion.text) : procedureNote
    },
    impressionTitle() {
      return this.$t('report.impression')
    },
    findingDescriptionsTitle() {
      return 'Finding descriptions'
    },
    keyImagesTitle() {
      return this.$t('report.key_images')
    },
    moreImagesTitle() {
      return this.$t('report.more_images')
    },
    hasFindings() {
      return Array.isArray(this.findings) && this.findings.length > 0
    },
    colonLlmTexts() {
      if (Array.isArray(this.getColonDescriptiveParagraph)) {
        return this.getColonDescriptiveParagraph
      }
      if (typeof this.getColonDescriptiveParagraph === 'string' && this.getColonDescriptiveParagraph) {
        return [this.getColonDescriptiveParagraph]
      }
      return []
    },
    gastroLlmEntries() {
      if (this.getGastroDescriptiveParagraph && typeof this.getGastroDescriptiveParagraph === 'object') {
        return Object.entries(this.getGastroDescriptiveParagraph)
      }
      if (typeof this.getGastroDescriptiveParagraph === 'string' && this.getGastroDescriptiveParagraph) {
        return [['', this.getGastroDescriptiveParagraph]]
      }
      return []
    },
    findingsWithImages() {
      const allImages = this.examination?.all || []
      return (this.findings || []).map((finding, idx) => ({
        ...finding,
        imageGroupName: `${this.$t('finding')}${idx + 1}: ${finding.name}`,
        images: allImages
          .filter((item) => Number(item.findingID) === Number(finding.id) && item.status == PRINT_STATUS_YES)
          .reverse(),
      }))
    },
    additionalImages() {
      const allImages = this.examination?.all || []
      const findingIds = new Set((this.findings || []).map((finding) => Number(finding.id)))
      return allImages
        .filter((item) => item.status == PRINT_STATUS_YES && !findingIds.has(Number(item.findingID)))
        .reverse()
    },
  }
}
</script>

<style lang="scss" scoped>
.report-top-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, auto);
  gap: 24px;
  align-items: start;
}

.report-top-grid__content,
.report-top-grid__map,
.report-top-grid__procedure {
  min-width: 0;
}

@media print {
  .report-top-grid {
    grid-template-columns: minmax(0, 1fr) minmax(300px, auto);
    gap: 18px;
  }
}
</style>
